import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { FeaturedItinerary, MasterData } from '../../types';
import { itineraryService } from '../../services/itinerary';
import { masterDataService } from '../../services/masterData';
import api from '../../services/api';
import FeaturedModal from './FeaturedModal';
import ItineraryCard from './ItineraryCard';
import FilterPanel from './FilterPanel';
import ItineraryFormModal from '../ItineraryFormModal';
import PreviewModal from '../PreviewModal';
import SetPriceModal from '../SetPriceModal';
import { Loader2, RefreshCw, Search, Filter, Grid, List, Plus, Star } from 'lucide-react';
import { useToast } from './ToastContainer';
import { useAuthContext } from '../../contexts/AuthContext';
import VersionsModal from './VersionsModal';

interface ModernBalifyPageProps {
  onOpenOperationalCost?: (itineraryId: number) => void;
}

const ModernBalifyPage: React.FC<ModernBalifyPageProps> = ({ onOpenOperationalCost }) => {
  const location = useLocation();
  const [itineraries, setItineraries] = useState<any[]>([]);
  const [featuredItineraries, setFeaturedItineraries] = useState<FeaturedItinerary[]>([]);
  const [masterData, setMasterData] = useState<MasterData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedItinerary, setSelectedItinerary] = useState<any | null>(null);
  const [action, setAction] = useState<'create' | 'edit' | 'copy' | null>(null);
  const [previewItinerary, setPreviewItinerary] = useState<any | null>(null);
  const [setPriceItinerary, setSetPriceItinerary] = useState<any | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showFeatured, setShowFeatured] = useState(false);
  const { showToast } = useToast();
  const { user } = useAuthContext();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<any>(null);
  const [versionsItineraryId, setVersionsItineraryId] = useState<number | null>(null);
  const limit = 12;

  useEffect(() => {
    loadData(true);
  }, []);

  // Handle URL parameters for edit/copy functionality
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const editId = params.get('edit');
    const copyId = params.get('copy');
    const create = params.get('create');
    const versionOf = params.get('version_of');
    
    if (editId && masterData) {
      handleEdit({ id: parseInt(editId) });
    } else if (copyId && masterData) {
      handleCopy({ id: parseInt(copyId) });
    } else if (versionOf && masterData) {
      handleCreateVersionFromModal(parseInt(versionOf));
    } else if (create === 'true' && masterData) {
      setAction('create');
    }
  }, [location.search, masterData]);

  const loadData = async (reset: boolean = false, forceMasterData: boolean = false) => {
    try {
      if (reset) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      
      const currentOffset = reset ? 0 : offset;
      
      const [itins, featured, master] = await Promise.all([
        itineraryService.getRecentItineraries(limit, currentOffset).catch((error) => {
          if (error?.response?.status === 401) {
            console.warn('Authentication required for recent itineraries');
          }
          return [];
        }),
        reset ? itineraryService.getFeaturedItineraries().catch(() => []) : Promise.resolve(featuredItineraries),
        (!masterData || forceMasterData) ? masterDataService.getMasterData() : Promise.resolve(masterData)
      ]);
      
      const newItins = itins || [];
      
      if (reset) {
        setItineraries(newItins);
        setOffset(newItins.length);
        setFeaturedItineraries(featured || []);
        if (master) setMasterData(master);
      } else {
        setItineraries(prev => {
          const existingIds = new Set(prev.map(i => i.id || i.itinerary_id));
          const uniqueNew = newItins.filter(i => !existingIds.has(i.id || i.itinerary_id));
          return [...prev, ...uniqueNew];
        });
        setOffset(prev => prev + newItins.length);
      }
      
      setHasMore(newItins.length === limit);
    } catch (error) {
      console.error('Error loading data:', error);
      if (reset) {
        setItineraries([]);
        setFeaturedItineraries([]);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const reloadMasterData = async () => {
    try {
      setRefreshing(true);
      const master = await masterDataService.getMasterData();
      setMasterData(master);
      // Also reload itinerary list
      loadData(true);
      showToast('Master data and cache reloaded successfully', 'success');
    } catch (error) {
      console.error('Error reloading master data:', error);
      showToast('Failed to reload master data', 'error');
    } finally {
      setRefreshing(false);
    }
  };

  const handleSearch = async (query: string) => {
    if (!query.trim()) {
      loadData(true);
      return;
    }

    try {
      setLoading(true);
      setSearchQuery(query);
      
      const results = await itineraryService.searchItineraries({ q: query });
      
      setItineraries(results || []);
      setOffset(0);
      setHasMore(false);
    } catch (error) {
      console.error('Error searching:', error);
      setItineraries([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterApply = async (filters: any) => {
    try {
      setLoading(true);
      const params: any = { limit: 20 };
      if (filters.status) params.status = filters.status;
      if (filters.dateFrom) params.date_from = filters.dateFrom;
      if (filters.dateTo) params.date_to = filters.dateTo;
      if (filters.agencyName) params.agency_name = filters.agencyName;
      
      // Advanced pax filtering
      if (filters.paxOperator === 'between' && filters.paxMin && filters.paxMax) {
        params.pax_operator = 'between';
        params.pax_min = parseInt(filters.paxMin);
        params.pax_max = parseInt(filters.paxMax);
      } else if (filters.noOfPax && filters.paxOperator) {
        params.pax_operator = filters.paxOperator;
        params.no_of_pax = parseInt(filters.noOfPax);
      }
      
      // Advanced children filtering
      if (filters.childrenOperator === 'between' && filters.childrenMin && filters.childrenMax) {
        params.children_operator = 'between';
        params.children_min = parseInt(filters.childrenMin);
        params.children_max = parseInt(filters.childrenMax);
      } else if (filters.noOfChildren && filters.childrenOperator) {
        params.children_operator = filters.childrenOperator;
        params.no_of_children = parseInt(filters.noOfChildren);
      }
      
      const results = await itineraryService.searchItineraries(params);
      setItineraries((results || []).slice(0, 20));
      setOffset(0);
      setHasMore(false);
      setActiveFilters(filters);
      setSearchQuery('');
    } catch (error) {
      console.error('Error filtering:', error);
      setItineraries([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedItinerary(null);
    setAction('create');
  };

  const handleEdit = async (itinerary: any) => {
    const status = itinerary.cost_breakdown?.status || itinerary.status || 'PENDING_CONFIRMATION';
    
    // Prevent editing CONVERTED itineraries for non-admins
    if (status === 'CONVERTED' && user?.role !== 'ADMIN') {
      showToast('Cannot edit CONVERTED itinerary', 'error');
      return;
    }
    
    try {
      const id = itinerary.id || itinerary.itinerary_id;
      const itin = await itineraryService.getItinerary(id);
      setSelectedItinerary(itin);
      setAction('edit');
    } catch (error) {
      console.error('Error loading itinerary:', error);
    }
  };

  const handleCreateVersion = async (itinerary: any) => {
    const id = itinerary.id || itinerary.itinerary_id;
    setVersionsItineraryId(id);
  };

  const handleCreateVersionFromModal = async (id: number) => {
    try {
      const itin = await itineraryService.getItinerary(id);
      try {
        const qRes = await api.get(`/itinerary-versions/${id}/quotation`);
        if (qRes.data.success && qRes.data.quotation_number) {
          itin.quotation_number = qRes.data.quotation_number;
          const ver = qRes.data.versions?.find((v: any) => v.itinerary_id === id);
          itin.version_number = ver?.version_number;
        }
      } catch {}
      itin.itinerary_id = -1;
      itin.status = 'PENDING_CONFIRMATION';
      itin._version_of = id;
      if (itin.cost_breakdown) itin.cost_breakdown.status = 'PENDING_CONFIRMATION';
      setSelectedItinerary(itin);
      setAction('copy');
    } catch (error) {
      console.error('Error preparing version:', error);
    }
  };

  const handleCopy = async (itinerary: any) => {
    try {
      const id = itinerary.id || itinerary.itinerary_id;
      const itin = await itineraryService.getItinerary(id);
      itin.itinerary_id = -1;
      itin.basic_info.agency_id = -1;
      itin.basic_info.itinerary_name = '';
      itin.basic_info.lead_guest = '';
      itin.status = 'PENDING_CONFIRMATION';
      if (itin.cost_breakdown) {
        itin.cost_breakdown.status = 'PENDING_CONFIRMATION';
      }
      setSelectedItinerary(itin);
      setAction('copy');
    } catch (error) {
      console.error('Error copying itinerary:', error);
    }
  };

  const handlePreview = async (itinerary: any) => {
    try {
      const id = itinerary.id || itinerary.itinerary_id;
      const itin = await itineraryService.getItinerary(id);
      setPreviewItinerary(itin);
    } catch (error) {
      console.error('Error loading itinerary for preview:', error);
    }
  };

  const handleDownload = async (id: number) => {
    try {
      await itineraryService.downloadItinerary(id);
      showToast('Itinerary downloaded successfully', 'success');
    } catch (error: any) {
      console.error('Error downloading itinerary:', error);
      const errorMessage = error?.message || error?.response?.data?.detail || 'Failed to download itinerary';
      showToast(errorMessage, 'error');
    }
  };

  const handleSetPrice = async (itinerary: any) => {
    try {
      const id = itinerary.id || itinerary.itinerary_id;
      const itin = await itineraryService.getItinerary(id);
      setSetPriceItinerary(itin);
    } catch (error) {
      console.error('Error loading itinerary for pricing:', error);
      showToast('Failed to load itinerary', 'error');
    }
  };

  const handleAgentDownload = async (id: number) => {
    try {
      await itineraryService.downloadAgentItinerary(id);
      showToast('Agent itinerary downloaded successfully', 'success');
    } catch (error: any) {
      console.error('Error downloading agent itinerary:', error);
      const errorMessage = error?.message || error?.response?.data?.detail || 'Failed to download agent itinerary';
      showToast(errorMessage, 'error');
    }
  };

  const [statusChangeConfirm, setStatusChangeConfirm] = useState<{id: number, newStatus: string, currentStatus: string} | null>(null);

  const handleStatusChange = async (id: number, newStatus: string, currentStatus: string) => {
    setStatusChangeConfirm({ id, newStatus, currentStatus });
  };

  const confirmStatusChange = async () => {
    if (!statusChangeConfirm) return;
    
    const { id, newStatus, currentStatus } = statusChangeConfirm;
    setStatusChangeConfirm(null);
    
    try {
      console.log('=== STATUS CHANGE START ===');
      console.log('Itinerary ID:', id);
      console.log('Current Status:', currentStatus);
      console.log('New Status:', newStatus);
      
      let result;
      if (newStatus === 'CONVERTED') {
        // Use full conversion endpoint for CONVERTED status
        console.log('Using full conversion process');
        result = await itineraryService.convertItinerary(id);
      } else {
        // Use status-only endpoint for other status changes
        console.log('Using status-only update');
        result = await itineraryService.updateStatus(id, newStatus);
      }
      console.log('Status update result:', result);
      console.log('=== STATUS CHANGE SUCCESS ===');
      
      showToast('Status updated successfully', 'success');
      loadData(true);
    } catch (error: any) {
      console.error('=== STATUS CHANGE ERROR ===');
      console.error('Error object:', error);
      console.error('Error message:', error?.message);
      console.error('Error response:', error?.response);
      console.error('Error response data:', error?.response?.data);
      showToast(error?.response?.data?.detail || 'Failed to update status', 'error');
      loadData(true); // Reload to revert UI state
    }
  };

  const handleUseFeatured = async (featuredId: number) => {
    try {
      const featured = featuredItineraries.find(f => f.id === featuredId);
      if (featured) {
        const itin = await itineraryService.getItinerary(featured.itinerary_id);
        itin.itinerary_id = -1;
        itin.basic_info.agency_id = -1;
        itin.basic_info.itinerary_name = '';
        itin.basic_info.lead_guest = '';
        itin.status = 'PENDING_CONFIRMATION';
        if (itin.cost_breakdown) {
          itin.cost_breakdown.status = 'PENDING_CONFIRMATION';
        }
        setSelectedItinerary(itin);
        setAction('copy');
      }
    } catch (error) {
      console.error('Error using featured itinerary:', error);
    }
  };

  const handleFormClose = () => {
    setSelectedItinerary(null);
    setAction(null);
    // Clear URL parameters
    window.history.replaceState({}, '', '/balify');
    loadData(true);
  };

  if (loading && !masterData) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="flex items-center justify-center py-32">
          <div className="text-center">
            <Loader2 className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  const getFilterSummary = () => {
    if (!activeFilters) return '';
    const parts = [];
    if (activeFilters.dateFrom && activeFilters.dateTo) {
      parts.push(`Trips between ${activeFilters.dateFrom} and ${activeFilters.dateTo}`);
    } else if (activeFilters.dateFrom) {
      parts.push(`Trips from ${activeFilters.dateFrom}`);
    } else if (activeFilters.dateTo) {
      parts.push(`Trips until ${activeFilters.dateTo}`);
    }
    if (activeFilters.status) parts.push(`Status: ${activeFilters.status}`);
    if (activeFilters.noOfPax) parts.push(`${activeFilters.noOfPax} Adults`);
    if (activeFilters.noOfChildren) parts.push(`${activeFilters.noOfChildren} Children`);
    if (activeFilters.agencyName) parts.push(`Agency: ${activeFilters.agencyName}`);
    return parts.join(' • ');
  };

  return (
    <div className="h-screen bg-gray-50 flex flex-col">
      <div className="w-full px-3 sm:px-6 lg:px-8 py-3 sm:py-6 lg:py-8 flex-1 overflow-y-auto">
      {/* Header */}
      <div className="mb-4 sm:mb-6 animate-fade-in">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
          Travel Itineraries
        </h1>
        <p className="text-sm sm:text-base text-gray-600">Create and manage beautiful travel experiences</p>
      </div>

      {/* Action Bar */}
      <div className="mb-4 sm:mb-6 flex flex-col gap-3 sm:gap-4 animate-slide-up">
        {/* Search */}
        <div className="w-full">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
              <input
                type="text"
                placeholder="Search itineraries..."
                value={activeFilters ? getFilterSummary() : searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch(searchQuery)}
                className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2 sm:py-3 bg-white border border-gray-300 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
              />
            </div>
            <button
              onClick={() => handleSearch(searchQuery)}
              className="px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 text-white rounded-lg sm:rounded-xl hover:bg-blue-700 transition-all font-medium whitespace-nowrap text-sm"
            >
              Search
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowFeatured(true)}
            className="px-3 sm:px-4 py-2 sm:py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg sm:rounded-xl hover:shadow-lg transition-all flex items-center gap-1 sm:gap-2 text-sm"
          >
            <Star className="w-4 h-4 sm:w-5 sm:h-5 fill-white" />
            <span className="hidden sm:inline">Templates</span>
          </button>

          <button
            onClick={() => setShowFilters(true)}
            className="px-3 sm:px-4 py-2 sm:py-3 bg-white border border-gray-300 rounded-lg sm:rounded-xl hover:bg-gray-50 transition-all flex items-center gap-1 sm:gap-2 text-sm"
          >
            <Filter className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="hidden sm:inline">Filter</span>
          </button>

          <button
            onClick={reloadMasterData}
            className="px-3 sm:px-4 py-2 sm:py-3 bg-white border border-gray-300 rounded-lg sm:rounded-xl hover:bg-gray-50 transition-all flex items-center gap-1 sm:gap-2 text-sm"
            title="Reload Master Data & Clear Cache"
          >
            <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="hidden sm:inline">Reload</span>
          </button>

          <div className="hidden sm:flex bg-white border border-gray-300 rounded-xl overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-3 transition-all ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-50'}`}
            >
              <Grid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-3 transition-all ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-50'}`}
            >
              <List className="w-5 h-5" />
            </button>
          </div>

          <button
            onClick={handleCreate}
            className="px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 text-white rounded-lg sm:rounded-xl hover:bg-blue-700 hover:shadow-lg transition-all flex items-center gap-1 sm:gap-2 font-medium text-sm"
          >
            <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="hidden sm:inline">New</span>
            <span className="sm:hidden">New</span>
          </button>
        </div>
      </div>

      {/* Results Header */}
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <div>
          <h2 className="text-base sm:text-lg font-semibold text-gray-900">
            {searchQuery ? `Search results for "${searchQuery}"` : 'All Itineraries'}
          </h2>
          <p className="text-xs sm:text-sm text-gray-600">
            {itineraries.length} {itineraries.length === 1 ? 'itinerary' : 'itineraries'} found
          </p>
        </div>
        
        {refreshing && (
          <RefreshCw className="animate-spin h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="animate-spin h-8 w-8 text-blue-600" />
        </div>
      )}

      {/* Empty State */}
      {!loading && itineraries.length === 0 && (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
          <div className="text-7xl mb-6">✈️</div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3">No itineraries found</h3>
          <p className="text-gray-600 mb-8 max-w-md mx-auto">
            {searchQuery ? 'Try adjusting your search terms' : 'Get started by creating your first itinerary'}
          </p>
          <button
            onClick={handleCreate}
            className="px-8 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 hover:shadow-lg transition-all font-medium"
          >
            Create New Itinerary
          </button>
        </div>
      )}

      {/* Itineraries Grid/List */}
      {!loading && itineraries.length > 0 && (
        <>
          <div className={
            viewMode === 'grid' 
              ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8'
              : 'space-y-3 sm:space-y-4'
          }>
            {itineraries.map((itinerary) => (
              <ItineraryCard
                key={itinerary.id || itinerary.itinerary_id}
                itinerary={itinerary}
                onEdit={handleEdit}
                onCopy={handleCopy}
                onPreview={handlePreview}
                onDownload={handleDownload}
                onSetPrice={handleSetPrice}
                onAgentDownload={handleAgentDownload}
                onStatusChange={handleStatusChange}
                onOpenOperationalCost={onOpenOperationalCost}
                onVersionCreated={() => loadData(true)}
                onViewVersions={(itin: any) => setVersionsItineraryId(itin.id || itin.itinerary_id)}
                viewMode={viewMode}
              />
            ))}
          </div>

          {/* Load More */}
          {hasMore && (
            <div className="mt-6 sm:mt-8 text-center">
              <button
                onClick={() => loadData(false)}
                disabled={refreshing}
                className="px-6 sm:px-8 py-2 sm:py-3 bg-white border border-gray-300 rounded-lg sm:rounded-xl hover:bg-gray-50 disabled:opacity-50 transition-all font-medium text-sm"
              >
                {refreshing ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </>
      )}

      {/* Featured Modal */}
      {showFeatured && (
        <FeaturedModal
          featuredItineraries={featuredItineraries}
          onUse={handleUseFeatured}
          onClose={() => setShowFeatured(false)}
        />
      )}

      {/* Filter Panel */}
      <FilterPanel
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        onApplyFilters={handleFilterApply}
        onReset={() => loadData(true)}
      />

      {/* Form Modal */}
      {action && masterData && (
        <ItineraryFormModal
          itinerary={selectedItinerary}
          action={action}
          masterData={masterData}
          onClose={handleFormClose}
        />
      )}

      {/* Preview Modal */}
      {previewItinerary && (
        <PreviewModal
          data={previewItinerary}
          onClose={() => setPreviewItinerary(null)}
        />
      )}

      {/* Set Price Modal */}
      {setPriceItinerary && (
        <SetPriceModal
          itinerary={setPriceItinerary}
          onClose={() => setSetPriceItinerary(null)}
          onSuccess={() => {
            setSetPriceItinerary(null);
            showToast('Price updated successfully', 'success');
            loadData(true);
          }}
        />
      )}

      {/* Versions Modal */}
      {versionsItineraryId && (
        <VersionsModal
          itineraryId={versionsItineraryId}
          onClose={() => setVersionsItineraryId(null)}
          onNavigate={(id) => {
            setVersionsItineraryId(null);
            handleEdit({ id } as any);
          }}
          onCreateVersion={(id) => {
            setVersionsItineraryId(null);
            handleCreateVersionFromModal(id);
          }}
        />
      )}

      {/* Status Change Confirmation Dialog */}
      {statusChangeConfirm && (() => {
        const statusLabels: Record<string, string> = {
          PENDING_CONFIRMATION: 'Pending Confirmation',
          TENTATIVE: 'Tentative',
          TENTATIVE_BOOKING: 'Tentative',
          CONVERTED: 'Converted',
          PENDING_CANCELLATION: 'Pending Cancellation',
          CANCELED: 'Canceled'
        };
        return (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Change Status</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Change status from <strong>{statusLabels[statusChangeConfirm.currentStatus]}</strong> to <strong>{statusLabels[statusChangeConfirm.newStatus]}</strong>?
                  </p>
                  <div className="flex gap-3">
                    <button onClick={() => setStatusChangeConfirm(null)} className="btn-secondary flex-1">Cancel</button>
                    <button onClick={confirmStatusChange} className="btn-primary flex-1">Confirm</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
      </div>
    </div>
  );
};

export default ModernBalifyPage;